import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { vehicleAPI } from '../../services/api';
import { useSocket } from '../../context/SocketContext';
import { FiTruck, FiPlay, FiSquare, FiPlus, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import SearchableDropdown from '../Common/SearchableDropdown';
import {
  VEHICLE_TYPE_LABELS,
  VEHICLE_TYPE_VALUE_BY_LABEL,
  VEHICLE_TYPE_LABEL_BY_VALUE,
  getMakesForType,
  getModelsForTypeAndMake,
  getPlateOptions,
} from '../../data/vehicleCatalog';
import './VehicleSelector.css';

const EMPTY_VEHICLE = { plateNumber: '', type: '', make: '', model: '', phone: '' };

const norm = (value) => (value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

function fieldMatches(stored, selected) {
  const a = norm(stored);
  const b = norm(selected);
  if (!a || !b) return false;
  if (a === b) return true;
  return a.includes(b) || b.includes(a);
}

function mergeRegisteredVehicles(owned = [], map = []) {
  const seen = new Set();
  const merged = [];

  [...owned, ...map].forEach((vehicle) => {
    const key = vehicle._id || vehicle.id || vehicle.plateNumber;
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(vehicle);
  });

  return merged;
}

export default function VehicleSelector() {
  const [vehicles, setVehicles] = useState([]);
  const [mapVehicles, setMapVehicles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newVehicle, setNewVehicle] = useState(EMPTY_VEHICLE);
  const { activeVehicleId, startTracking, stopTracking } = useSocket();

  const loadVehicles = useCallback(async () => {
    try {
      const data = await vehicleAPI.getAll();
      setVehicles(data.vehicles || []);
    } catch {
      /* ignore */
    }
  }, []);

  const loadMapVehicles = useCallback(async () => {
    try {
      const data = await vehicleAPI.getAllForMap();
      setMapVehicles(data.vehicles || []);
    } catch {
      setMapVehicles([]);
    }
  }, []);

  useEffect(() => {
    loadVehicles();
    loadMapVehicles();
  }, [loadVehicles, loadMapVehicles]);

  useEffect(() => {
    if (showAdd) {
      loadVehicles();
      loadMapVehicles();
    }
  }, [showAdd, loadVehicles, loadMapVehicles]);

  const registeredVehicles = useMemo(
    () => mergeRegisteredVehicles(vehicles, mapVehicles),
    [vehicles, mapVehicles]
  );

  const makeOptions = useMemo(
    () => getMakesForType(newVehicle.type),
    [newVehicle.type]
  );

  const modelOptions = useMemo(
    () => getModelsForTypeAndMake(newVehicle.type, newVehicle.make),
    [newVehicle.type, newVehicle.make]
  );

  const matchingPlates = useMemo(() => {
    if (!newVehicle.type || !newVehicle.make || !newVehicle.model) return [];

    const registered = [...new Set(
      registeredVehicles
        .filter((v) =>
          fieldMatches(v.type, newVehicle.type)
          && fieldMatches(v.make, newVehicle.make)
          && fieldMatches(v.model, newVehicle.model)
        )
        .map((v) => v.plateNumber)
        .filter(Boolean)
    )];

    return getPlateOptions(registered);
  }, [registeredVehicles, newVehicle.type, newVehicle.make, newVehicle.model]);

  const handleTypeChange = (label) => {
    setNewVehicle({
      ...newVehicle,
      type: VEHICLE_TYPE_VALUE_BY_LABEL[label] || '',
      make: '',
      model: '',
      plateNumber: '',
    });
  };

  const handleMakeChange = (make) => {
    setNewVehicle({
      ...newVehicle,
      make,
      model: '',
      plateNumber: '',
    });
  };

  const handleModelChange = (model) => {
    setNewVehicle({
      ...newVehicle,
      model,
      plateNumber: '',
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await vehicleAPI.create(newVehicle);
      toast.success('Vehicle added');
      setNewVehicle(EMPTY_VEHICLE);
      setShowAdd(false);
      loadVehicles();
      loadMapVehicles();
    } catch (err) {
      toast.error(err?.message || 'Failed to add vehicle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    try {
      await vehicleAPI.remove(id);
      toast.success('Vehicle deleted');
      if (activeVehicleId === id) stopTracking();
      loadVehicles();
      loadMapVehicles();
    } catch (err) {
      toast.error(err?.message || 'Failed to delete vehicle');
    }
  };

  const typeLabel = VEHICLE_TYPE_LABEL_BY_VALUE[newVehicle.type] || '';

  const makeHint = !newVehicle.type ? 'Select Vehicle Type first' : 'No makes found';
  const modelHint = !newVehicle.make ? 'Select Make first' : 'No models found';
  const plateHint = !newVehicle.type
    ? 'Select Vehicle Type first'
    : !newVehicle.make
      ? 'Select Make first'
      : !newVehicle.model
        ? 'Select Model first'
        : 'Type a new plate number (e.g. KA01AB1234)';

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}><FiTruck /> Vehicles</h4>
        <button onClick={() => setShowAdd(!showAdd)} style={styles.addBtn}>
          <FiPlus /> Add
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="vehicle-add-form" style={styles.form}>
          <SearchableDropdown
            options={VEHICLE_TYPE_LABELS}
            value={typeLabel}
            onChange={handleTypeChange}
            placeholder="Select Vehicle Type"
            required
          />

          <SearchableDropdown
            options={makeOptions}
            value={newVehicle.make}
            onChange={handleMakeChange}
            placeholder="Select Make"
            required
            emptyMessage={makeHint}
          />

          <SearchableDropdown
            options={modelOptions}
            value={newVehicle.model}
            onChange={handleModelChange}
            placeholder="Select Model"
            required
            emptyMessage={modelHint}
          />

          <SearchableDropdown
            options={matchingPlates}
            value={newVehicle.plateNumber}
            onChange={(plateNumber) => setNewVehicle({ ...newVehicle, plateNumber })}
            placeholder="Select or type Plate Number"
            required
            allowCustom
            emptyMessage={plateHint}
          />

          <input
            type="tel"
            placeholder="Phone Number"
            value={newVehicle.phone}
            onChange={(e) => setNewVehicle({ ...newVehicle, phone: e.target.value })}
            required
            pattern="^\+?[\d\s-]{7,15}$"
            title="Enter a valid phone number"
            style={styles.input}
          />
          <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '8px 0' }}>
            Save Vehicle
          </button>
        </form>
      )}

      <div style={styles.list}>
        {vehicles.length === 0 && !showAdd && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
            No vehicles. Add one to start tracking.
          </p>
        )}
        {vehicles.map((v) => {
          const isActive = activeVehicleId === (v._id || v.id);
          return (
            <div key={v._id || v.id} style={{
              ...styles.vehicleItem,
              borderColor: isActive ? '#3b82f6' : 'var(--border-color)',
            }}>
              <div>
                <div style={styles.plate}>{v.plateNumber}</div>
                <div style={styles.meta}>
                  {v.type} {v.make && `| ${v.make}`} {v.model && v.model}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button
                  onClick={() => isActive ? stopTracking() : startTracking(v._id || v.id)}
                  style={{
                    ...styles.trackBtn,
                    background: isActive ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                    color: isActive ? '#ef4444' : '#22c55e',
                  }}
                >
                  {isActive ? <><FiSquare /> Stop</> : <><FiPlay /> Track</>}
                </button>
                <button
                  onClick={() => handleDelete(v._id || v.id)}
                  style={styles.deleteBtn}
                  title="Delete vehicle"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  container: {},
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
  },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: 'rgba(59,130,246,0.1)', color: '#3b82f6',
    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
  },
  form: {
    display: 'flex', flexDirection: 'column', gap: 8,
    padding: 12, background: 'var(--bg-tertiary)', borderRadius: 8, marginBottom: 12,
  },
  input: { padding: '8px 10px', fontSize: 13 },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  vehicleItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 12px', borderRadius: 8,
    border: '1px solid var(--border-color)',
    background: 'var(--bg-tertiary)',
    transition: 'all 0.2s',
  },
  plate: { fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' },
  meta: { fontSize: 12, color: 'var(--text-muted)', marginTop: 2 },
  trackBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    border: 'none', cursor: 'pointer',
  },
  deleteBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '6px 8px', borderRadius: 6, fontSize: 14,
    background: 'rgba(239,68,68,0.1)', color: '#ef4444',
    border: 'none', cursor: 'pointer',
    transition: 'all 0.2s',
  },
};
